import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import SchemaOptimizerChangeTooltip from "./SchemaOptimizerChangeTooltip";
import SchemaOptimizerHistorySelect from "./SchemaOptimizerHistorySelect";
import SchemaOptimizerIconButton from "./SchemaOptimizerIconButton";
import SchemaOptimizerWhatChanged from "./SchemaOptimizerWhatChanged";
import {
	buildChangeLineMap,
	displayLineRangeForChange,
	resolveChangeIndexForRow,
	schemaSnippetForChange,
} from "./schemaOptimizerChangeLog";
import {
	diffJsonStats,
	recommendedDisplayLines,
} from "./schemaOptimizerLineDiff";
import {
	applyManualRequiredToSchema,
	countManualDiffRows,
	markManualDiffRows,
} from "./schemaOptimizerManualRequired";
import {
	formatConfidence,
	formatJson,
	isGeneratedContentSource,
	jobSourceLabel,
	normalizeResult,
	shorthandUrl,
} from "./schemaOptimizerUtils";

function copyText(text) {
	if (!text) return;
	navigator.clipboard.writeText(text).then(
		() => toast.success("Copied to clipboard"),
		() => toast.error("Could not copy")
	);
}

function downloadJson(filename, text) {
	if (!text) return;
	const blob = new Blob([text], { type: "application/json" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	a.click();
	URL.revokeObjectURL(url);
}

export default function SchemaOptimizerResultView({
	job,
	websiteId,
	currentJobId,
	onSelectJobId,
	onRegenerate,
	regenerating,
}) {
	const result = useMemo(() => normalizeResult(job?.result), [job?.result]);
	const couldNotInfer = result?.changes?.couldNotInfer ?? [];
	const [tab, setTab] = useState("recommended");
	const [showManualRequired, setShowManualRequired] = useState(true);
	const [hoverTip, setHoverTip] = useState(null);
	const diffRef = useRef(null);
	const hideTipTimerRef = useRef(null);
	const activeChangeRef = useRef(null);

	const changeLog = result?.changes?.changeLog ?? [];
	const displayRecommendedSchema = useMemo(() => {
		if (!result?.recommendedSchema) return null;
		if (!showManualRequired || couldNotInfer.length === 0) {
			return result.recommendedSchema;
		}
		return applyManualRequiredToSchema(result.recommendedSchema, couldNotInfer);
	}, [result?.recommendedSchema, showManualRequired, couldNotInfer]);

	const stats = useMemo(
		() =>
			result && displayRecommendedSchema
				? diffJsonStats(result.originalSchema, displayRecommendedSchema)
				: { unchanged: 0, added: 0, replaced: 0, removed: 0 },
		[result, displayRecommendedSchema]
	);
	const displayLines = useMemo(() => {
		if (!result || !displayRecommendedSchema) return [];
		const rows = recommendedDisplayLines(
			result.originalSchema,
			displayRecommendedSchema
		);
		return showManualRequired ? markManualDiffRows(rows, couldNotInfer) : rows;
	}, [result, displayRecommendedSchema, showManualRequired, couldNotInfer]);

	const manualLineCount = useMemo(
		() => countManualDiffRows(displayLines),
		[displayLines]
	);

	const changeLineMap = useMemo(
		() =>
			displayRecommendedSchema
				? buildChangeLineMap(displayRecommendedSchema, changeLog)
				: new Map(),
		[displayRecommendedSchema, changeLog]
	);

	useEffect(() => {
		return () => {
			if (hideTipTimerRef.current) clearTimeout(hideTipTimerRef.current);
		};
	}, []);

	useEffect(() => {
		if (tab !== "recommended") {
			activeChangeRef.current = null;
			setHoverTip(null);
			if (hideTipTimerRef.current) {
				clearTimeout(hideTipTimerRef.current);
				hideTipTimerRef.current = null;
			}
		}
	}, [tab]);

	const rowsWithChange = useMemo(() => {
		return displayLines.map((row) => {
			const changeIndex = resolveChangeIndexForRow(
				row,
				changeLineMap,
				changeLog,
				displayRecommendedSchema
			);
			const entry = changeIndex != null ? changeLog[changeIndex] : null;
			return {
				...row,
				changeIndex,
				entry,
				isInteractive: entry != null || row.manualEntry != null,
				isHighlighted:
					row.type === "added" || row.type === "manual" || changeIndex != null,
			};
		});
	}, [displayLines, changeLineMap, changeLog, displayRecommendedSchema]);

	if (!result) {
		return <p className="text-muted mb-0">No schema result yet.</p>;
	}

	const recommendedStr = formatJson(
		showManualRequired ? displayRecommendedSchema : result.recommendedSchema
	);
	const shacl = result.shaclReport;

	const pageUrl = isGeneratedContentSource(job)
		? job?.domainUrl ?? job?.domain_url
		: result.url || job?.url;
	const sourceContent =
		job?.sourceContent ?? job?.source_content ?? job?.sourceContentPreview ?? null;
	const historyUrl = job?.url ?? result.url;

	function hoverTipKey(row) {
		if (row?.entry != null) return `change-${row.changeIndex}`;
		if (row?.manualEntry) return `manual-${row.lineNum}-${row.manualEntry.property}`;
		return null;
	}

	function showChangeTooltip(row) {
		if (!row?.entry && !row?.manualEntry) return;
		if (hideTipTimerRef.current) {
			clearTimeout(hideTipTimerRef.current);
			hideTipTimerRef.current = null;
		}
		const tipKey = hoverTipKey(row);
		if (tipKey && activeChangeRef.current === tipKey) return;

		if (row.manualEntry) {
			activeChangeRef.current = tipKey;
			setHoverTip({
				changeIndex: null,
				entry: row.manualEntry,
				lineStart: row.lineNum,
				lineEnd: row.lineNum,
				snippet: row.text?.trim() || "",
			});
			return;
		}

		const range = displayLineRangeForChange(
			rowsWithChange,
			row.changeIndex,
			row.entry
		);
		const start = range?.start ?? row.lineNum;
		const end = range?.end ?? row.lineNum;

		activeChangeRef.current = tipKey;
		setHoverTip({
			changeIndex: row.changeIndex,
			entry: row.entry,
			lineStart: start,
			lineEnd: end,
			snippet: schemaSnippetForChange(displayRecommendedSchema, row.entry),
		});
	}

	function scheduleHideTooltip() {
		if (hideTipTimerRef.current) clearTimeout(hideTipTimerRef.current);
		hideTipTimerRef.current = setTimeout(() => {
			activeChangeRef.current = null;
			setHoverTip(null);
		}, 120);
	}

	function handleDiffMouseMove(event) {
		const lineEl = event.target.closest?.(
			".schema-optimizer__json-diff-line--interactive"
		);
		if (!lineEl || !diffRef.current?.contains(lineEl)) {
			scheduleHideTooltip();
			return;
		}
		const lineNum = Number(lineEl.getAttribute("data-line-num"));
		if (Number.isNaN(lineNum)) {
			scheduleHideTooltip();
			return;
		}
		const row = rowsWithChange.find((r) => r.lineNum === lineNum);
		if (row?.isInteractive) showChangeTooltip(row);
		else scheduleHideTooltip();
	}

	function handleDiffMouseLeave(event) {
		const next = event.relatedTarget;
		if (next instanceof Node && diffRef.current?.contains(next)) return;
		if (hideTipTimerRef.current) clearTimeout(hideTipTimerRef.current);
		hideTipTimerRef.current = null;
		activeChangeRef.current = null;
		setHoverTip(null);
	}

	return (
		<div className="schema-optimizer__result">
			<div className="schema-optimizer__result-meta-bar">
				{pageUrl && !isGeneratedContentSource(job) && (
					<div className="schema-optimizer__meta-item schema-optimizer__meta-item--url">
						<span className="schema-optimizer__meta-label">URL</span>
						<a
							href={pageUrl}
							target="_blank"
							rel="noopener noreferrer"
							title={pageUrl}
						>
							{shorthandUrl(pageUrl)}
							<i className="fas fa-external-link-alt ms-1 schema-optimizer__meta-link-icon" />
						</a>
					</div>
				)}
				{isGeneratedContentSource(job) && (
					<div className="schema-optimizer__meta-item">
						<span className="schema-optimizer__meta-label">Source</span>
						<span className="schema-optimizer__meta-value">{jobSourceLabel(job)}</span>
					</div>
				)}
				{result.detectedDomain && (
					<div className="schema-optimizer__meta-item">
						<span className="schema-optimizer__meta-label">Domain</span>
						<span className="schema-optimizer__meta-value">{result.detectedDomain}</span>
					</div>
				)}
				{result.detectedVertical && (
					<div className="schema-optimizer__meta-item">
						<span className="schema-optimizer__meta-label">Vertical</span>
						<span className="schema-optimizer__meta-value">{result.detectedVertical}</span>
					</div>
				)}
				{formatConfidence(result.domainConfidence) && (
					<div className="schema-optimizer__meta-item">
						<span className="schema-optimizer__meta-label">Confidence</span>
						<span className="schema-optimizer__meta-value">
							{formatConfidence(result.domainConfidence)}
						</span>
					</div>
				)}
				{shacl && (
					<div className="schema-optimizer__meta-item schema-optimizer__meta-item--shacl">
						<span
							className={
								shacl.conforms
									? "schema-optimizer__shacl schema-optimizer__shacl--ok"
									: "schema-optimizer__shacl schema-optimizer__shacl--bad"
							}
						>
							{shacl.conforms
								? "SHACL conforms"
								: `${shacl.violationCount ?? shacl.violation_count ?? 0} SHACL violation(s)`}
						</span>
					</div>
				)}
			</div>

			<div className="schema-optimizer__result-card">
				<div className="schema-optimizer__result-card-toolbar">
					<div className="schema-optimizer__result-tabs" role="tablist">
						<button
							type="button"
							role="tab"
							className={`schema-optimizer__result-tab${
								tab === "recommended" ? " schema-optimizer__result-tab--active" : ""
							}`}
							onClick={() => setTab("recommended")}
						>
							Recommended schema
						</button>
						<button
							type="button"
							role="tab"
							className={`schema-optimizer__result-tab${
								tab === "content" ? " schema-optimizer__result-tab--active" : ""
							}`}
							onClick={() => setTab("content")}
						>
							Content
						</button>
					</div>
					<div className="schema-optimizer__result-toolbar-actions">
						{couldNotInfer.length > 0 && (
							<label className="schema-optimizer__manual-toggle">
								<input
									type="checkbox"
									className="schema-optimizer__manual-toggle-input"
									checked={showManualRequired}
									onChange={(e) => setShowManualRequired(e.target.checked)}
								/>
								<span className="schema-optimizer__manual-toggle-track" aria-hidden />
								<span className="schema-optimizer__manual-toggle-label">
									Manual add required
								</span>
							</label>
						)}
						{websiteId && historyUrl && onSelectJobId && (
							<SchemaOptimizerHistorySelect
								websiteId={websiteId}
								url={historyUrl}
								currentJobId={currentJobId}
								onSelectJobId={onSelectJobId}
							/>
						)}
						{onRegenerate && (
							<SchemaOptimizerIconButton
								title="Regenerate"
								iconClass="fas fa-sync-alt"
								accent
								disabled={regenerating}
								onClick={onRegenerate}
							/>
						)}
						<SchemaOptimizerIconButton
							title={tab === "content" ? "Copy content" : "Copy JSON"}
							iconClass="far fa-copy"
							onClick={() =>
								copyText(tab === "content" ? sourceContent : recommendedStr)
							}
							disabled={tab === "content" ? !sourceContent : !recommendedStr}
						/>
						<SchemaOptimizerIconButton
							title={tab === "content" ? "Download content" : "Download JSON"}
							iconClass="fas fa-download"
							onClick={() =>
								tab === "content"
									? downloadJson("page-content.txt", sourceContent)
									: downloadJson("recommended-schema.json", recommendedStr)
							}
							disabled={tab === "content" ? !sourceContent : !recommendedStr}
						/>
					</div>
				</div>

				{tab === "recommended" && (
					<>
						<div className="schema-optimizer__diff-stats small">
							<span>{stats.unchanged} unchanged</span>
							<span className="schema-optimizer__diff-stat-add">+{stats.added} added</span>
							{stats.replaced > 0 && (
								<span className="schema-optimizer__diff-stat-replace">
									~{stats.replaced} replaced
								</span>
							)}
							{showManualRequired && manualLineCount > 0 && (
								<span className="schema-optimizer__diff-stat-manual">
									{manualLineCount} manual
								</span>
							)}
							{(changeLog.length > 0 ||
								(showManualRequired && manualLineCount > 0)) && (
								<span className="schema-optimizer__diff-hint ms-auto">
									Hover highlighted lines for details
								</span>
							)}
						</div>
						<div
							className="schema-optimizer__json-diff"
							ref={diffRef}
							onMouseMove={handleDiffMouseMove}
							onMouseLeave={handleDiffMouseLeave}
						>
							{rowsWithChange.length === 0 ? (
								<pre className="schema-optimizer__json-pre schema-optimizer__json-pre--light mb-0">
									{recommendedStr || "—"}
								</pre>
							) : (
								<pre className="schema-optimizer__json-diff-pre mb-0">
									{rowsWithChange.map((row) => (
										<div
											key={`${row.lineNum}-${row.text}`}
											data-line-num={row.lineNum}
											className={`schema-optimizer__json-diff-line schema-optimizer__json-diff-line--${row.type}${
												row.isHighlighted
													? " schema-optimizer__json-diff-line--highlighted"
													: ""
											}${
												row.isInteractive
													? " schema-optimizer__json-diff-line--interactive"
													: ""
											}`}
										>
											<span className="schema-optimizer__json-diff-gutter">
												<span className="schema-optimizer__json-diff-marker">
													{row.marker}
												</span>
												<span className="schema-optimizer__json-diff-ln">
													{row.lineNum}
												</span>
											</span>
											<code className="schema-optimizer__json-diff-code">{row.text}</code>
										</div>
									))}
								</pre>
							)}
						</div>
					</>
				)}

				{tab === "content" && (
					<div className="schema-optimizer__content-pane">
						{sourceContent ? (
							<pre className="schema-optimizer__content-pre mb-0">{sourceContent}</pre>
						) : (
							<p className="text-muted small mb-0 px-3 py-3">
								No page content stored for this job.
							</p>
						)}
					</div>
				)}
			</div>

			<SchemaOptimizerWhatChanged changes={result.changes} />

			{result.notes && (
				<div className="schema-optimizer__result-notes small text-muted mt-3">{result.notes}</div>
			)}

			{tab === "recommended" &&
				hoverTip &&
				typeof document !== "undefined" &&
				createPortal(
					<SchemaOptimizerChangeTooltip
						docked
						entry={hoverTip.entry}
						schemaSnippet={hoverTip.snippet}
						lineStart={hoverTip.lineStart}
						lineEnd={hoverTip.lineEnd}
					/>,
					document.body
				)}
		</div>
	);
}
